// Analisis butir soal (teori tes klasik) dan laporan soal dari siswa. Fungsi murni, dipakai
// server (riwayat, admin) dan halaman admin.
//   - Tingkat kesukaran p = proporsi jawaban benar (mudah > 0,8; sulit < 0,3).
//   - Daya beda D = p kelompok atas − p kelompok bawah (dibagi dari nilai ronde: 27% teratas dan
//     27% terbawah). D < 0 berarti anak yang nilainya tinggi justru sering salah: periksa kuncinya.
//   - Sebaran pilihan: pengecoh yang tidak pernah dipilih tidak berfungsi; pengecoh yang lebih sering
//     dipilih daripada kunci perlu dicek (bisa membingungkan atau kuncinya keliru).

const ENTITAS = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", nbsp: ' ' };
/** Teks polos dari HTML soal (untuk mencocokkan pilihan dan cuplikan). */
export const teksPolos = (s, n = 120) => String(s ?? '').replace(/<span class="fr"><span class="fn">([^<]*)<\/span><span class="fd">([^<]*)<\/span><\/span>/g, '$1/$2').replace(/<(?:br|\/?(?:p|li|div|tr|td|h\d))\b[^>]*>/gi, ' ').replace(/<[^>]*>/g, '').replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (_, k) => ENTITAS[k]).replace(/\s+/g, ' ').trim().slice(0, n);

const POLA_ID = /^[A-Za-z0-9:_-]{1,80}$/;
/** Id soal untuk analisis: id bank/pengembangan, atau per topik untuk soal generator. */
export function idAnalisis(q) {
  if (q?.sumber === 'generator' || !q?.id) return q?.gen ? `gen:${q.gen}` : null;
  return POLA_ID.test(String(q.id)) ? String(q.id) : null;
}

/** Baris tabel `jawaban` dari satu rekaman hasil latihan (POST /api/riwayat). */
export function barisJawaban(rekaman, nama, ts = Date.now(), akun = null) {
  const out = [];
  for (const s of rekaman?.soal || []) {
    const q = s?.q || {};
    if (s.skip || !['pg', 'mcma', 'bs'].includes(q.type)) continue;
    const id = idAnalisis(q);
    if (!id) continue;
    let pilihan = null;
    if (q.type === 'pg' && Number.isInteger(s.jawaban)) pilihan = [teksPolos(q.options?.[s.jawaban], 100)];
    if (q.type === 'mcma' && Array.isArray(s.jawaban)) pilihan = s.jawaban.filter(Number.isInteger).map(i => teksPolos(q.options?.[i]?.t, 100)).sort();
    if (q.type === 'bs' && Array.isArray(s.jawaban)) pilihan = s.jawaban.map(v => (v == null ? '-' : v ? '1' : '0'));
    const judul = q.bacaan?.judul ? `[${teksPolos(q.bacaan.judul, 40)}] ` : '';
    out.push({
      ts, nama: String(nama || '').slice(0, 30), akun, mapel: String(rekaman.mapel || '').slice(0, 40), soal_id: id,
      sumber: ['bank', 'pengembangan', 'generator'].includes(q.sumber) ? q.sumber : (id.startsWith('gen:') ? 'generator' : 'bank'),
      topik: String(q.gen || String(q.topic || '').replace(/^bank:/, '')).slice(0, 40) || null, tipe: q.type,
      ok: s.ok ? 1 : 0, ms: Math.max(0, Math.min(36e5, Math.round(Number(s.ms) || 0))),
      pilihan: pilihan ? JSON.stringify(pilihan).slice(0, 600) : null, nilai: Math.round(Number(rekaman.nilai) || 0),
      cuplikan: id.startsWith('gen:') ? null : (judul + teksPolos(q.text, 110)).slice(0, 160),
    });
  }
  return out;
}

/** Batas nilai kelompok atas & bawah (27%) dari sebaran nilai [{ nilai, n }]. */
export function batasKelompok(sebaran) {
  const urut = [...sebaran].filter(x => Number.isFinite(x.nilai)).sort((a, b) => a.nilai - b.nilai);
  const total = urut.reduce((a, x) => a + x.n, 0);
  if (!total) return { bawah: -1, atas: 101 };
  const persentil = p => { let c = 0; for (const x of urut) { c += x.n; if (c >= total * p) return x.nilai; } return urut.at(-1).nilai; };
  let bawah = persentil(0.27), atas = persentil(0.73);
  if (atas <= bawah) { atas = bawah + 1; }
  return { bawah, atas };
}

export const MIN_ANALISIS = 10; // jumlah jawaban minimal sebelum soal ditandai

/**
 * Hitung metrik dan tanda untuk satu soal.
 * @param {{n, b, ms, na, ba, nb, bb, laporan?}} x agregat dari D1 (+ jumlah laporan)
 */
export function nilaiButir(x) {
  const n = x.n || 0, p = n ? x.b / n : null;
  const D = x.na >= 3 && x.nb >= 3 ? x.ba / x.na - x.bb / x.nb : null;
  const kesukaran = p == null ? null : p > 0.8 ? 'mudah' : p < 0.3 ? 'sulit' : 'sedang';
  const dayaBeda = D == null ? null : D < 0 ? 'negatif' : D < 0.2 ? 'kurang' : D < 0.4 ? 'cukup' : 'baik';
  const tanda = [];
  if (x.laporan) tanda.push({ id: 'laporan', bobot: 5, teks: `${x.laporan} laporan dari siswa` });
  if (n >= MIN_ANALISIS) {
    if (D != null && D < 0) tanda.push({ id: 'beda-negatif', bobot: 4, teks: 'Daya beda negatif: anak bernilai tinggi justru sering salah. Periksa kuncinya.' });
    if (p < 0.3) tanda.push({ id: 'sulit', bobot: 3, teks: 'Sering salah (kurang dari 30% benar)' });
    if (D != null && D >= 0 && D < 0.2) tanda.push({ id: 'beda-kurang', bobot: 1, teks: 'Daya beda rendah' });
    if (p > 0.95) tanda.push({ id: 'mudah', bobot: 1, teks: 'Hampir semua benar (lebih dari 95%)' });
  }
  return { n, p, D, kesukaran, dayaBeda, rataMs: x.ms ?? null, tanda, bobot: tanda.reduce((a, t) => a + t.bobot, 0) };
}

/**
 * Sebaran pilihan untuk soal pg/mcma dibanding pilihan di sumber soal.
 * @param {string[]} opsi teks polos pilihan di sumber; @param {number[]} kunci indeks jawaban benar
 * @param {Array<{pilihan: string, c: number}>} hitung baris agregat (pilihan = JSON array teks)
 */
export function sebaranPilihan(opsi, kunci, hitung, n) {
  const jumlah = opsi.map(() => 0);
  for (const r of hitung) {
    let arr; try { arr = JSON.parse(r.pilihan); } catch (e) { continue; }
    for (const t of Array.isArray(arr) ? arr : []) { const i = opsi.indexOf(t); if (i >= 0) jumlah[i] += r.c; }
  }
  const hasil = opsi.map((t, i) => ({ teks: t, kunci: kunci.includes(i), c: jumlah[i], persen: n ? Math.round((jumlah[i] / n) * 100) : 0 }));
  const tanda = [];
  if (n >= 15) {
    const tidak = hasil.filter(h => !h.kunci && h.c === 0);
    if (tidak.length) tanda.push({ id: 'pengecoh-mati', bobot: 1, teks: `${tidak.length} pengecoh tidak pernah dipilih` });
    const maksKunci = Math.max(...hasil.filter(h => h.kunci).map(h => h.c), 0);
    if (kunci.length === 1 && hasil.some(h => !h.kunci && h.c > maksKunci)) tanda.push({ id: 'pengecoh-kuat', bobot: 3, teks: 'Ada pengecoh yang lebih sering dipilih daripada kunci' });
  }
  return { pilihan: hasil, tanda };
}

/* ---------------- laporan soal dari siswa ---------------- */

export const ALASAN_LAPORAN = {
  kunci: 'Kunci jawaban sepertinya salah',
  bingung: 'Soal membingungkan atau ada salah ketik',
  pembahasan: 'Pembahasan kurang jelas',
  tampilan: 'Gambar atau teks tidak tampil dengan benar',
  lainnya: 'Lainnya',
};
export const STATUS_LAPORAN = ['baru', 'selesai', 'abaikan'];
export const BATAS_LAPORAN = 32 * 1024;
export const MAKS_LAPORAN_HARIAN = 20;

const teks = (v, n) => (typeof v === 'string' ? v.replace(/[\u0000-\u001f]+/g, ' ').trim().slice(0, n) : '');
// Teks panjang (bacaan): pertahankan pergantian paragraf.
const teksParagraf = (v, n) => (typeof v === 'string' ? v.replace(/\r\n?/g, '\n').replace(/[\u0000-\u0009\u000b-\u001f]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim().slice(0, n) : '');

/** Periksa & rapikan kiriman laporan. Mengembalikan { galat } atau { laporan }. */
export function rapikanLaporan(b) {
  if (!b || typeof b !== 'object') return { galat: 'Data tidak valid.' };
  if (!ALASAN_LAPORAN[b.alasan]) return { galat: 'Pilih alasan laporan.' };
  const s = b.soal || {};
  if (!['pg', 'mcma', 'bs'].includes(s.tipe) || !teks(s.teks, 10)) return { galat: 'Data soal tidak lengkap.' };
  const daftar = (a, n) => (Array.isArray(a) ? a.slice(0, 8).map(x => teks(x, n)) : []);
  return {
    laporan: {
      alasan: b.alasan, catatan: teks(b.catatan, 300),
      soal: {
        id: teks(s.id, 80) || null, mapel: teks(s.mapel, 40), sumber: teks(s.sumber, 20), topik: teks(s.topik, 40), judul: teks(s.judul, 80),
        tipe: s.tipe, teks: teks(s.teks, 1200), pilihan: daftar(s.pilihan, 300), kunci: teks(s.kunci, 600),
        bacaan: s.bacaan ? { judul: teks(s.bacaan.judul, 120), teks: teksParagraf(s.bacaan.teks, 6000) } : null,
      },
      jawabanAnak: teks(b.jawabanAnak, 600),
    },
  };
}
